// Type B — map_mcq: map provides geographic context; student answers MCQ.

import { Check, X as XIcon, ArrowRight } from 'lucide-react';
import Button from '../../ui/Button';
import { cn } from '../../../lib/cn';

export default function MapMcqPanel({
  question,
  state,                 // { selectedOptionId, submitted, isCorrect }
  onSelectOption,
  onSubmit,
  onNext,
  isLast,
}) {
  const { selectedOptionId, submitted, isCorrect } = state;
  const correctOption = question.options.find((o) => o.correct);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-5 overflow-y-auto">
        <p className="text-base leading-relaxed text-gray-900 dark:text-gray-100">
          {question.prompt}
        </p>

        <div className="space-y-2">
          {question.options.map((option) => {
            const isChosen = selectedOptionId === option.id;
            const showCorrect = submitted && option.correct;
            const showWrong = submitted && isChosen && !option.correct;

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => !submitted && onSelectOption(option.id)}
                disabled={submitted}
                className={cn(
                  'w-full text-left px-4 py-3 rounded-lg border text-sm',
                  'transition-all duration-150 ease-out',
                  'focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 dark:focus:ring-offset-gray-900',
                  // Base state
                  !submitted && !isChosen &&
                    'border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-600 dark:hover:bg-gray-700/50',
                  // Selected pre-submit
                  !submitted && isChosen &&
                    'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950/30 dark:text-amber-200',
                  // Correct reveal
                  showCorrect &&
                    'border-l-4 border-green-600 bg-green-50 text-green-900 dark:border-green-500 dark:bg-green-950/40 dark:text-green-200',
                  // Wrong reveal on student's pick
                  showWrong &&
                    'border-l-4 border-red-600 bg-red-50 text-red-900 dark:border-red-500 dark:bg-red-950/40 dark:text-red-200',
                  // Non-chosen, non-correct after submit: dim
                  submitted && !option.correct && !isChosen &&
                    'border-gray-200 bg-white text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500',
                  submitted && 'cursor-default',
                )}
              >
                <div className="flex items-start gap-2">
                  {submitted && option.correct && (
                    <Check className="w-4 h-4 mt-0.5 shrink-0" />
                  )}
                  {submitted && isChosen && !option.correct && (
                    <XIcon className="w-4 h-4 mt-0.5 shrink-0" />
                  )}
                  <span className="flex-1">{option.text}</span>
                </div>
              </button>
            );
          })}
        </div>

        {submitted && (
          <div className="space-y-3">
            <div
              className={cn(
                'rounded-lg px-4 py-3 text-sm font-medium',
                isCorrect
                  ? 'bg-green-50 text-green-900 dark:bg-green-950/40 dark:text-green-200'
                  : 'bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200',
              )}
            >
              {isCorrect ? 'Correct.' : `The correct answer is: "${correctOption?.text}"`}
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/70 px-4 py-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
              {question.explanation}
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
        {submitted ? (
          <Button onClick={onNext} variant="primary" className="w-full">
            {isLast ? 'See results' : 'Next question'}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            variant="primary"
            className="w-full"
            disabled={!selectedOptionId}
          >
            Submit
          </Button>
        )}
      </div>
    </div>
  );
}
